import {useState, useEffect, useMemo, useCallback, useRef} from 'react'
import {useMutation, useQuery} from '@tanstack/react-query'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {useDialog} from '@/hooks/useDialog'
import {useStepFocus} from '@/hooks/use-step-focus'
import {ArrowLeft, ArrowRight, CheckCircle, AlertCircle, Play, Users, HelpCircle, Plus, Minus, Equal, Search} from 'lucide-react'
import {useNavigate, Link} from 'react-router-dom'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {inBrowserRuleConditionError} from '@/lib/cel/ruleengine/cel/RuleConditionValidator'
import {SmallText} from '@/components/shared/SmallText'
import {WizardStepHeading} from '@/components/shared/WizardStepHeading'
import {Strong} from '@/components/shared/Strong'
import {Stack} from '@/components/shared/Stack'
import {routes} from '@/lib/routes'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Textarea} from '@/components/ui/textarea'
import {ExpressionEditor} from '@/components/ui/expression-editor'
import {SCHEMA_TYPES_IDENTITY} from '@/types/expression'
import {Label} from '@/components/ui/label'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {OwnersPicker} from '@/components/shared/OwnersPicker'
import {AssociationEditor} from '@/components/shared/AssociationEditor'
import type {AssociationColumn} from '@/components/shared/AssociationEditor'
import {Alert, AlertDescription, AlertTitle} from '@/components/ui/alert'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {RuleRequestType, TestResultAction} from '@/types/auto-assign-rule-request'
import type {
    AutoAssignRuleRequestCreateRequest,
    ValidateExpression,
    RuleTest,
    TestResultItem
} from '@/types/auto-assign-rule-request'
import type {AccessProfile, AccessProfileListItem} from '@/types/access-profile'
import {AutoAssignRuleType, AutoAssignRuleRequestCreateRequestFields, AutoAssignWizardStep, AUTO_ASSIGN_WIZARD_STEP_COUNT} from '@/types/auto-assign-rule'
import type {AutoAssignRuleProfile} from '@/types/auto-assign-rule'
import type {FunctionGroup} from '@/types/function-group'
import {ProvisioningTaskHistoryResponseFields} from '@/types/effective-access'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {ButtonSpinner} from '@/components/shared/ButtonSpinner'
import {PageLayout} from '@/components/shared/PageLayout'
import {MutedText} from '@/components/shared/MutedText'
import {Icon} from '@/components/shared/Icon'
import {StatValue} from '@/components/shared/StatValue'
import {StatBox} from '@/components/shared/StatBox'
import type {WizardStep as WizardStepType} from '@/components/shared/WizardStepper'
import {InlineRow} from '@/components/shared/InlineRow'
import {CodeBlock} from '@/components/shared/CodeBlock'
import {ScrollableBox} from '@/components/shared/ScrollableBox'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {StatusText} from '@/components/shared/StatusText'
import {IconText} from '@/components/shared/IconText'
import {IconInput} from '@/components/shared/IconInput'
import {InfoBanner} from '@/components/shared/InfoBanner'
import {useAutoAssignRuleRequestCreate} from '@/features/auto-assign/hooks/useAutoAssignRuleRequestCreate'
import {useAutoAssignRuleRequestWizardMode} from '@/features/auto-assign/hooks/useAutoAssignRuleRequestWizardMode'
import {useAutoAssignRuleRequestWizardForm} from '@/features/auto-assign/hooks/useAutoAssignRuleRequestWizardForm'

// eslint-disable-next-line i18next/no-literal-string
const AffectedFilter = { ALL: 'ALL', ...TestResultAction } as const
type AffectedFilter = typeof AffectedFilter[keyof typeof AffectedFilter]

interface AutoAssignRuleRequestWizardProps {
    mode?: 'create' | 'edit'
    requestId?: string
    initialData?: {
        name: string
        description?: string
        conditionExpr: string
        priority: number
        removalGraceDays: number
        owners?: Array<{ownerPartyId: string; ownerPartyDisplayName?: string; responsibility: string}>
        userRisk?: number | null
        profileIds: string[]
        version?: number
    }
}

// Steps are computed dynamically inside the component to adapt step 4 label to the selected rule type

const EXPRESSION_HELP = `
**Expression Syntax:**
- \`identity.type == 'WORKFORCE'\` - Match identity type
- \`identity.lifecycleState == 'ACTIVE'\` - Match lifecycle state
- \`identity.party.parents.exists(p, p.attributes.code == 'ENG')\` - Match by parent org-unit code
- \`party.displayName CONTAINS 'Admin'\` - String contains
- \`party.type == 'PERSON'\` - Match party type

**Operators:**
- Comparison: \`==\`, \`!=\`, \`>\`, \`>=\`, \`<\`, \`<=\`
- String: \`CONTAINS\`, \`STARTS_WITH\`, \`ENDS_WITH\`
- Null: \`IS NULL\`, \`IS NOT NULL\`
- List: \`IN ['value1', 'value2']\`
- Logical: \`AND\`, \`OR\`, \`NOT\`
- Hierarchy: \`identity has/hasParent/hasChild\`

**Hierarchy:**
- \`identity has functionGroup with name == 'X'\` - Assigned function group matches
- \`identity hasParent functionGroup with name == 'X'\` - Function group or ancestors match
- \`identity hasChild party with type == 'TEAM'\` - Party descendants match

**Examples:**
\`\`\`
identity.type == 'WORKFORCE' AND identity.lifecycleState == 'ACTIVE'

(identity.type == 'EMPLOYEE' OR identity.type == 'CONTRACTOR')
AND identity.party.parents.exists(p, p.attributes.code IN ['ENG', 'IT'])

identity.type == 'WORKFORCE'
AND identity hasParent party with displayName == 'IT Department'
\`\`\`
`.trim()

export function AutoAssignRuleRequestCreateWizard({
    mode = 'create', requestId, initialData}: AutoAssignRuleRequestWizardProps) {
    const services = useServices()
    const i18n = useI18n()
    const {functionGroupTypes, autoAssignRuleResponsibilityTypes} = useAppConfig()
    usePageTitle(mode === 'edit' ? i18n.autoAssignRuleRequests.editPageTitle : i18n.autoAssignRuleRequests.createPageTitle)
    const navigate = useNavigate()
    const wizardMode = useAutoAssignRuleRequestWizardMode()
    const {isUpdateFromUrl, ruleId, seededRuleType, initialStep} = wizardMode

    const isEditMode = mode === 'edit'

    const {data: ruleData, isLoading: ruleLoading} = useQuery({
        queryKey: ['auto-assign-rule', ruleId],
        queryFn: () => services.autoAssignRules.detail(ruleId!),
        enabled: !!isUpdateFromUrl,
    })

    const {data: ruleProfiles = [], isLoading: ruleProfilesLoading} = useQuery({
        queryKey: ['auto-assign-rule-profiles', ruleId],
        queryFn: () => services.autoAssignRules.profiles(ruleId!),
        enabled: !!isUpdateFromUrl,
    })

    const effectiveInitialData = useMemo(() => isUpdateFromUrl && ruleData ? {
        name: ruleData.name,
        description: ruleData.description || undefined,
        conditionExpr: ruleData.conditionExpr,
        priority: ruleData.priority,
        removalGraceDays: ruleData.removalGraceDays,
        owners: ruleData.owners.map(o => ({
            ownerPartyId: o.ownerPartyId,
            ownerPartyDisplayName: o.ownerPartyName,
            responsibility: o.responsibility,
        })),
        userRisk: ruleData.userRisk,
        profileIds: ruleProfiles.map((p: AutoAssignRuleProfile) => p.accessProfile.id),
        version: ruleData.version,
    } : initialData, [isUpdateFromUrl, ruleData, ruleProfiles, initialData])

    const [currentStep, setCurrentStep] = useState<number>(initialStep)
    const form = useAutoAssignRuleRequestWizardForm({initialRuleType: seededRuleType})
    const {
        name, description, owners, priority, removalGraceDays, userRisk, conditionExpr,
        ruleType, profileIds: selectedProfileIds, functionGroupIds: selectedFunctionGroupIds,
        version, setName, setDescription, setPriority, setRemovalGraceDays,
        setUserRisk, setConditionExpr, setRuleType, addProfile, removeProfile,
        addFunctionGroup, removeFunctionGroup, seed,
    } = form
    const [initialized, setInitialized] = useState(false)
    const [ownerLabels, setOwnerLabels] = useState<Record<string, string>>({})

    useEffect(() => {
        if (effectiveInitialData && !initialized) {
            const seededOwners = effectiveInitialData.owners?.length
                ? effectiveInitialData.owners.map(o => ({
                    ownerPartyId: o.ownerPartyId,
                    responsibility: o.responsibility,
                }))
                : undefined
            const seededLabels: Record<string, string> = {}
            for (const o of effectiveInitialData.owners ?? []) {
                if (o.ownerPartyDisplayName) seededLabels[o.ownerPartyId] = o.ownerPartyDisplayName
            }
            seed({
                name: effectiveInitialData.name || '',
                description: effectiveInitialData.description || '',
                ...(seededOwners ? {owners: seededOwners} : {}),
                priority: effectiveInitialData.priority ?? 100,
                removalGraceDays: effectiveInitialData.removalGraceDays ?? 7,
                userRisk: effectiveInitialData.userRisk?.toString() || '',
                conditionExpr: effectiveInitialData.conditionExpr || '',
                profileIds: new Set(effectiveInitialData.profileIds || []),
                version: effectiveInitialData.version ?? 0,
            })
            setOwnerLabels(prev => ({...prev, ...seededLabels}))
            setInitialized(true)
        }
    }, [effectiveInitialData, initialized, seed])

    // UI state
    const [profileSearchQuery, setProfileSearchQuery] = useState('')
    const [functionGroupSearchQuery, setFunctionGroupSearchQuery] = useState('')
    const [validationResult, setValidationResult] = useState<ValidateExpression | null>(null)
    const [testResults, setTestResults] = useState<RuleTest | null>(null)
    const [testError, setTestError] = useState<string | null>(null)
    const [showHelp, setShowHelp] = useState(false)
    const [testResultFilter, setTestResultFilter] = useState<AffectedFilter>(AffectedFilter.ALL)
    const [testResultSearch, setTestResultSearch] = useState('')
    const testResultsDialog = useDialog()

    // Compute step labels based on rule type

    const STEP_BASIC = 'basicStep'
    const STEP_EXPRESSION = 'expressionStep'
    const STEP_TEST = 'testStep'
    const STEP_ASSIGNMENT = 'assignmentStep'
    const STEP_REVIEW = 'reviewStep'
    const steps = useMemo(() => [
        {id: STEP_BASIC, title: i18n.autoAssignRuleRequests.stepBasicInfo, description: i18n.autoAssignRuleRequests.stepBasicInfoDesc},
        {id: STEP_EXPRESSION, title: i18n.autoAssignRuleRequests.stepExpression, description: i18n.autoAssignRuleRequests.stepExpressionDesc},
        {id: STEP_TEST, title: i18n.autoAssignRuleRequests.stepTest, description: i18n.autoAssignRuleRequests.stepTestDesc},
        {id: STEP_ASSIGNMENT, title: ruleType === AutoAssignRuleType.FUNCTION_GROUP
                ? i18n.autoAssignRuleRequests.stepFunctionGroups : i18n.autoAssignRuleRequests.stepProfiles,
            description: ruleType === AutoAssignRuleType.FUNCTION_GROUP
                ? i18n.autoAssignRuleRequests.stepFunctionGroupsDesc : i18n.autoAssignRuleRequests.stepProfilesDesc},
        {id: STEP_REVIEW, title: i18n.autoAssignRuleRequests.stepReview, description: i18n.autoAssignRuleRequests.stepReviewDesc},
    ], [ruleType, i18n])

    const stepContainerRef = useStepFocus(currentStep, String(steps[currentStep].title))



    // Access profile search - only auto-assignable profiles
    const {data: profileSearchData} = useQuery({
        queryKey: ['access-profiles', 'search', 'auto-assignable', profileSearchQuery],
        // eslint-disable-next-line i18next/no-literal-string
        queryFn: () => services.accessProfiles.list({search: profileSearchQuery, size: 20, status: 'ACTIVE', autoAssignable: 'true'}),

    })

    // Function group search - only auto-assignable groups
    const {data: functionGroupSearchData} = useQuery({
        queryKey: ['function-groups', 'search', 'auto-assignable', functionGroupSearchQuery],
        queryFn: () => services.functionGroups.list({q: functionGroupSearchQuery, size: 20, autoAssignable: true}),
        enabled: ruleType === AutoAssignRuleType.FUNCTION_GROUP,
    })

    // Fetch selected function groups
    const {data: selectedFunctionGroups = []} = useQuery({
        queryKey: ['function-groups', 'by-ids', Array.from(selectedFunctionGroupIds)],
        queryFn: async () => {
            if (selectedFunctionGroupIds.size === 0) return []
            const promises = Array.from(selectedFunctionGroupIds).map(id =>

                services.functionGroups.detail(id).catch(() => null)
            )
            const results = await Promise.all(promises)
            return results.filter((fg): fg is FunctionGroup => fg !== null)
        },
        enabled: selectedFunctionGroupIds.size > 0,
    })

    // Fetch selected profiles
    const {data: selectedProfiles = []} = useQuery({
        queryKey: ['access-profiles', 'by-ids', Array.from(selectedProfileIds)],
        queryFn: async () => {
            if (selectedProfileIds.size === 0) return []
            const promises = Array.from(selectedProfileIds).map(id =>
                services.accessProfiles.detail(id).catch(() => null)
            )
            const results = await Promise.all(promises)
            return results.filter((p): p is AccessProfile => p !== null)
        },
        enabled: selectedProfileIds.size > 0,
    })

    // WHY: the editor validates per keystroke; in-flight backend responses can
    // arrive out of order, so we tag the latest validated expression and drop
    // stale responses — otherwise a late verdict for an earlier intermediate
    // (e.g. `identity.attributes.`) clobbers the result for the final text.
    const latestValidatedExpr = useRef('')

    // Validation mutation
    const validateMutation = useMutation({
        mutationFn: (expression: string) =>
            services.autoAssignRules.validateExpression({expression}),
        onSuccess: (data, expression) => {
            if (expression === latestValidatedExpr.current) {
                setValidationResult(data)
            }
        },
    })

    // WHY: voice the structural "not a boolean condition" error instantly,
    // in-browser, without a backend round-trip (issue 1161). Syntax / type /
    // ACL verdicts still come from the authoritative backend call below — the
    // in-browser check only short-circuits the definite structural rejections.
    const runValidation = useCallback((expression: string) => {
        latestValidatedExpr.current = expression
        const shapeError = inBrowserRuleConditionError(expression)
        if (shapeError !== null) {
            setValidationResult({valid: false, errorMessage: shapeError, errorPosition: -1})
        } else {
            validateMutation.mutate(expression)
        }
    }, [validateMutation])

    useEffect(() => {
        if (currentStep === AutoAssignWizardStep.CONDITION && conditionExpr.trim()) {
            runValidation(conditionExpr)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentStep])

    const testMutation = useMutation({
        mutationFn: () =>
            services.autoAssignRules.testExpression({
                conditionExpr,
                profileIds: Array.from(selectedProfileIds),
                limit: 50,
                ruleId: ruleId || undefined,
            }),
        onSuccess: (data) => {
            setTestResults(data)
            setTestError(null)
        },
        onError: (err: Error) => {
            setTestError(err.message)
            setTestResults(null)
        },
    })

    const createMutation = useAutoAssignRuleRequestCreate()

    const updateMutation = useEntityMutation({
        mutationFn: (data: AutoAssignRuleRequestCreateRequest & { version: number }) =>
            services.autoAssignRules.updateRequest(requestId!, data),
        invalidate: [['rule-request', requestId], ['rule-requests']],
        successMessage: i18n.autoAssignRuleRequests.requestUpdated,
        onSuccess: () => {
            navigate(routes.accessGovernance.autoAssignRuleRequests.detail(requestId!))
        },
    })

    const saveMutation = isEditMode ? updateMutation : createMutation

    const handleExpressionChange = (value: string) => {
        setConditionExpr(value)
        setTestResults(null)
        setTestError(null)
        if (value.trim()) {
            runValidation(value)
        } else {
            setValidationResult(null)
        }
    }

    const isStepValid = (step: number): boolean => {
        switch (step) {
            case AutoAssignWizardStep.BASICS:
                return !!name.trim()
                    && owners.length > 0
                    && owners.every(o => o.ownerPartyId.length > 0 && o.responsibility.length > 0)
            case AutoAssignWizardStep.CONDITION:
                return !!conditionExpr.trim() && !!validationResult?.valid
            case AutoAssignWizardStep.TEST:
                return true
            case AutoAssignWizardStep.PROFILES:
                return ruleType === AutoAssignRuleType.FUNCTION_GROUP
                    ? selectedFunctionGroupIds.size > 0
                    : selectedProfileIds.size > 0
            case AutoAssignWizardStep.REVIEW:
                return true
            default:
                return false
        }
    }

    const goNext = () => {
        if (currentStep < AUTO_ASSIGN_WIZARD_STEP_COUNT - 1 && isStepValid(currentStep)) {
            setCurrentStep(currentStep + 1)
        }
    }

    const goBack = () => {
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1)
        }
    }

    // Submit
    const handleSubmit = () => {
        const isFunctionGroupRule = ruleType === AutoAssignRuleType.FUNCTION_GROUP
        const baseData = {
            name,
            description: description || undefined,
            conditionExpr,
            priority,
            removalGraceDays,
            owners,
            userRisk: userRisk ? parseInt(userRisk, 10) : undefined,
            ruleType,
            profileIds: isFunctionGroupRule ? undefined : Array.from(selectedProfileIds),
            functionGroupIds: isFunctionGroupRule ? Array.from(selectedFunctionGroupIds) : undefined,
        }
        if (isEditMode) {
            updateMutation.mutate({...baseData, requestType: RuleRequestType.CREATE, version})
        } else if (isUpdateFromUrl && ruleId) {
            createMutation.mutate({
                ...baseData,
                requestType: RuleRequestType.UPDATE,
                ruleId,
            })
        } else {
            createMutation.mutate({...baseData, requestType: RuleRequestType.CREATE})
        }
    }

    // Render step content
    const renderStepContent = () => {
        switch (currentStep) {
            case AutoAssignWizardStep.BASICS:
                return renderBasicInfo()
            case AutoAssignWizardStep.CONDITION:
                return renderExpression()
            case AutoAssignWizardStep.TEST:
                return renderTest()
            case AutoAssignWizardStep.PROFILES:
                return renderAssignment()
            case AutoAssignWizardStep.REVIEW:
                return renderReview()
            default:
                return null
        }
    }

    const renderBasicInfo = () => (
        <Stack gap="lg">
            <div>
                <Label htmlFor={AutoAssignRuleRequestCreateRequestFields.name}>{i18n.autoAssignRuleRequests.ruleNameLabel}</Label>
                <Input
                    id={AutoAssignRuleRequestCreateRequestFields.name}
                    data-testid="input-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={i18n.autoAssignRuleRequests.ruleNamePlaceholder}
                    className="mt-1"
                />
            </div>

            <div>
                <Label htmlFor={AutoAssignRuleRequestCreateRequestFields.description}>{i18n.common.description}</Label>
                <Textarea
                    id={AutoAssignRuleRequestCreateRequestFields.description}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder={i18n.autoAssignRuleRequests.descriptionPlaceholder}
                    className="mt-1"
                    rows={3}
                />
            </div>

            <div>
                <Label>{i18n.autoAssignRuleRequests.ownerPartyLabel}</Label>
                <OwnersPicker
                    owners={owners}
                    onOwnersChange={form.setOwners}
                    responsibilityTypes={autoAssignRuleResponsibilityTypes}
                    ownerLabels={ownerLabels}
                    onOwnerLabelsChange={setOwnerLabels}
                />
                <MutedText size="xs" className="mt-1 block">
                    {i18n.autoAssignRuleRequests.ownerPartyDesc}
                </MutedText>
            </div>

            <div>
                <Label>{i18n.autoAssignRuleRequests.ruleTypeLabel}</Label>
                <InlineRow className="mt-1">
                    <Button
                        type="button"
                        variant={ruleType === AutoAssignRuleType.ACCESS_PROFILE ? 'default' : 'outline'}
                        onClick={() => setRuleType(AutoAssignRuleType.ACCESS_PROFILE)}
                        data-testid="btn-rule-type-access-profile"
                    >
                        {i18n.autoAssignRuleRequests.accessProfileRule}
                    </Button>
                    <Button
                        type="button"
                        variant={ruleType === AutoAssignRuleType.FUNCTION_GROUP ? 'default' : 'outline'}
                        onClick={() => setRuleType(AutoAssignRuleType.FUNCTION_GROUP)}
                        data-testid="btn-rule-type-function-group"
                    >
                        {i18n.autoAssignRuleRequests.functionGroupRule}
                    </Button>
                </InlineRow>
                <MutedText size="xs" className="mt-1 block">
                    {i18n.autoAssignRuleRequests.ruleTypeDesc}
                </MutedText>
            </div>

            <DetailGrid columns={3}>
                <div>
                    <Label htmlFor={AutoAssignRuleRequestCreateRequestFields.priority}>{i18n.autoAssignRuleRequests.priorityLabel}</Label>
                    <Input
                        id={AutoAssignRuleRequestCreateRequestFields.priority}
                        type="number"
                        value={priority}
                        onChange={(e) => setPriority(parseInt(e.target.value, 10) || 0)}
                        min={0}
                        max={1000}
                        className="mt-1"
                    />
                    <MutedText size="xs" className="mt-1 block">{i18n.autoAssignRuleRequests.priorityDesc}</MutedText>
                </div>

                <div>
                    <Label htmlFor={AutoAssignRuleRequestCreateRequestFields.removalGraceDays}>{i18n.autoAssignRuleRequests.removalGraceDaysLabel}</Label>
                    <Input
                        id={AutoAssignRuleRequestCreateRequestFields.removalGraceDays}
                        type="number"
                        value={removalGraceDays}
                        onChange={(e) => setRemovalGraceDays(parseInt(e.target.value, 10) || 0)}
                        min={0}
                        max={365}
                        className="mt-1"
                    />
                    <MutedText size="xs" className="mt-1 block">{i18n.autoAssignRuleRequests.removalGraceDaysDesc}</MutedText>
                </div>

                <div>
                    <Label htmlFor={AutoAssignRuleRequestCreateRequestFields.userRisk}>{i18n.autoAssignRuleRequests.userRiskLabel}</Label>
                    <Input
                        id={AutoAssignRuleRequestCreateRequestFields.userRisk}
                        type="number"
                        value={userRisk}
                        onChange={(e) => setUserRisk(e.target.value)}
                        min={0}
                        max={100}
                        placeholder={i18n.common.optional as string}
                        className="mt-1"
                    />
                    <MutedText size="xs" className="mt-1 block">{i18n.autoAssignRuleRequests.userRiskDesc}</MutedText>
                </div>
            </DetailGrid>
        </Stack>
    )

    const renderExpression = () => (
        <Stack>
            <InlineRow justify="between">
                <Label htmlFor="conditionExpr">{i18n.autoAssignRuleRequests.conditionExprLabel}</Label>
                <Collapsible open={showHelp} onOpenChange={setShowHelp}>
                    <CollapsibleTrigger asChild>
                        <Button variant="ghost" size="sm" type="button" data-testid="btn-toggle-help">
                            <Icon icon={HelpCircle} className="mr-1"/>
                            {showHelp ? i18n.autoAssignRuleRequests.hideHelp : i18n.autoAssignRuleRequests.showHelp}
                        </Button>
                    </CollapsibleTrigger>
                </Collapsible>
            </InlineRow>

            <Collapsible open={showHelp} onOpenChange={setShowHelp}>
                <CollapsibleContent>
                    <CodeBlock>{EXPRESSION_HELP}</CodeBlock>
                </CollapsibleContent>
            </Collapsible>

            <ExpressionEditor
                value={conditionExpr}
                onChange={handleExpressionChange}
                placeholder={i18n.autoAssignRuleRequests.conditionExprPlaceholder as string}
                rows={6}
                schemaTypes={SCHEMA_TYPES_IDENTITY}
                data-testid="expression-editor"
            />

            {/* Validation feedback */}
            {conditionExpr.trim() && validationResult && (
                <StatusText
                    tone={validationResult.valid ? 'success' : 'destructive'}
                    size="sm"
                    data-testid={validationResult.valid ? 'expression-valid' : 'expression-invalid'}
                >
                    <IconText icon={validationResult.valid ? CheckCircle : AlertCircle}>
                        {validationResult.valid ? (
                            i18n.autoAssignRuleRequests.expressionValid
                        ) : (
                            <>
                                {validationResult.errorMessage}
                                {/* eslint-disable-next-line i18next/no-literal-string -- technical position indicator */}
                                {validationResult.errorPosition > 0 && ` (position ${validationResult.errorPosition})`}
                            </>
                        )}
                    </IconText>
                </StatusText>
            )}
        </Stack>
    )

    // Filter and search test results
    const filteredTestResults = testResults?.items?.filter((item: TestResultItem) => {
        if (testResultFilter !== AffectedFilter.ALL && item.action !== testResultFilter) {
            return false
        }
        // Filter by search
        if (testResultSearch) {
            const search = testResultSearch.toLowerCase()
            return (
                item.identityDisplayName?.toLowerCase().includes(search) ||
                item.partyDisplayName?.toLowerCase().includes(search)
            )
        }
        return true
    }) || []

    const getActionBadge = (action: TestResultItem[typeof ProvisioningTaskHistoryResponseFields.action]) => {
        switch (action) {
            case TestResultAction.ADD:
                return <Badge variant={BadgeVariant.SUCCESS}><InlineRow gap="xs"><Icon icon={Plus} size="xs"/>{i18n.common.add}</InlineRow></Badge>
            case TestResultAction.REMOVE:
                return <Badge variant="destructive"><InlineRow gap="xs"><Icon icon={Minus} size="xs"/>{i18n.common.remove}</InlineRow></Badge>
            case TestResultAction.UNCHANGED:
                return <Badge variant="secondary"><InlineRow gap="xs"><Icon icon={Equal} size="xs"/>{i18n.common.unchanged}</InlineRow></Badge>
        }
    }

    const renderTest = () => (
        <Stack>
            <Stack gap="sm">
                <MutedText block centered>{i18n.autoAssignRuleRequests.testYourExpression}</MutedText>
                <Button
                    onClick={() => testMutation.mutate()}
                    disabled={testMutation.isPending || !validationResult?.valid}
                    size="lg"
                    data-testid="btn-run-test"
                >
                    {testMutation.isPending ? (
                        <>
                            <ButtonSpinner/>
                            {i18n.autoAssignRules.testing}
                        </>
                    ) : (
                        <>
                            <Icon icon={Play} className="mr-2"/>
                            {i18n.autoAssignRuleRequests.runTest}
                        </>
                    )}
                </Button>
            </Stack>

            {testError && (
                <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4"/>
                    <AlertTitle>{i18n.autoAssignRules.testFailed}</AlertTitle>
                    <AlertDescription>{testError}</AlertDescription>
                </Alert>
            )}

            {testResults && (
                <Stack>
                    <Alert data-testid="test-results-success" className="bg-status-success-subtle border-green-200">
                        <InlineRow justify="between" align="start" className="w-full">
                            <InlineRow gap="sm" className="items-start">
                                <Icon icon={Users} className="text-status-success mt-0.5"/>
                                <div>
                                    <AlertTitle className="text-status-success">{i18n.autoAssignRuleRequests.testCompleted}</AlertTitle>
                                    <AlertDescription className="text-status-success">
                                        {i18n.autoAssignRuleRequests.testCompletedDesc({total: testResults.totalEvaluated, matched: testResults.matched})}
                                    </AlertDescription>
                                </div>
                            </InlineRow>
                            {testResults.items && testResults.items.length > 0 && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                        setTestResultFilter(AffectedFilter.ALL)
                                        setTestResultSearch('')
                                        testResultsDialog.open()
                                    }}
                                    data-testid="btn-view-test-results"
                                >
                                    <Icon icon={Users} className="mr-2"/>
                                    {i18n.autoAssignRuleRequests.viewCount({count: testResults.items.length})}
                                </Button>
                            )}
                        </InlineRow>
                    </Alert>

                    {/* Summary Stats */}
                    <DetailGrid columns={3} responsive={false}>
                        <StatBox variant="success">
                            <StatValue value={testResults.toAdd} variant="success"/>
                            <MutedText size="xs">{i18n.autoAssignRuleRequests.wouldBeAdded}</MutedText>
                        </StatBox>
                        <StatBox variant="danger">
                            <StatValue value={testResults.toRemove} variant="danger"/>
                            <MutedText size="xs">{i18n.autoAssignRuleRequests.wouldBeRemoved}</MutedText>
                        </StatBox>
                        <StatBox variant="neutral">
                            <StatValue value={testResults.unchanged}/>
                            <MutedText size="xs">{i18n.autoAssignRuleRequests.unchanged}</MutedText>
                        </StatBox>
                    </DetailGrid>
                </Stack>
            )}

            {/* Test Results Dialog */}
            <Dialog open={testResultsDialog.isOpen} onOpenChange={testResultsDialog.bind.onOpenChange}>
                <DialogContent className="max-w-3xl h-[600px]">
                    <DialogHeader className="flex-shrink-0">
                        <DialogTitle>{i18n.autoAssignRules.affectedIdentities}</DialogTitle>
                    </DialogHeader>
                    <Stack gap="md" align="stretch" className="flex-1 min-h-0">
                        {/* Filter buttons */}
                        <InlineRow gap="sm" wrap className="flex-shrink-0">
                            <Button
                                variant={testResultFilter === AffectedFilter.ALL ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setTestResultFilter(AffectedFilter.ALL)}
                                data-testid="btn-filter-all"
                            >
                                {i18n.autoAssignRuleRequests.allCount({count: testResults?.items?.length || 0})}
                            </Button>
                            <Button
                                variant={testResultFilter === AffectedFilter.ADD ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setTestResultFilter(AffectedFilter.ADD)}
                                    className={testResultFilter !== AffectedFilter.ADD ? `text-status-success border-green-200 hover:bg-status-success-subtle` : ''}
                                data-testid="btn-filter-add"
                            >
                                <Icon icon={Plus} size="xs" className="mr-1"/>
                                {i18n.autoAssignRuleRequests.addCount({count: testResults?.toAdd || 0})}
                            </Button>
                            <Button
                                variant={testResultFilter === AffectedFilter.REMOVE ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setTestResultFilter(AffectedFilter.REMOVE)}
                                className={testResultFilter !== AffectedFilter.REMOVE ? `text-status-danger border-red-200 hover:bg-status-danger-subtle` : ''}
                                data-testid="btn-filter-remove"
                            >
                                <Icon icon={Minus} size="xs" className="mr-1"/>
                                {i18n.autoAssignRuleRequests.removeCount({count: testResults?.toRemove || 0})}
                            </Button>
                            <Button
                                variant={testResultFilter === AffectedFilter.UNCHANGED ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setTestResultFilter(AffectedFilter.UNCHANGED)}
                                className={testResultFilter !== AffectedFilter.UNCHANGED ? `text-gray-700 border-gray-200 hover:bg-gray-50` : ''}
                                data-testid="btn-filter-unchanged"
                            >
                                <Icon icon={Equal} size="xs" className="mr-1"/>
                                {i18n.autoAssignRuleRequests.unchangedCount({count: testResults?.unchanged || 0})}
                            </Button>
                            <IconInput
                                icon={Search}
                                inputSize="sm"
                                // eslint-disable-next-line i18next/no-literal-string -- CSS classes
                                widthClass="w-64 ml-auto"
                                placeholder={i18n.autoAssignRules.searchIdentities}
                                value={testResultSearch}
                                onChange={(e) => setTestResultSearch(e.target.value)}
                            />
                        </InlineRow>

                        {/* Table */}
                        <ScrollableBox grow flush>
                            <Table>
                                <TableHeader sticky>
                                    <TableRow>
                                        <TableHead data-testid="col-identity">{i18n.autoAssignRules.identity}</TableHead>
                                        <TableHead data-testid="col-party">{i18n.autoAssignRules.party}</TableHead>
                                        <TableHead className="w-28" data-testid="col-action">{i18n.autoAssignRules.action}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filteredTestResults.length === 0 ? (
                                        <TableEmptyState
                                            colSpan={3}
                                            hasFilters={false}
                                            matchMessage={i18n.autoAssignRules.noIdentitiesMatch as string}
                                            emptyMessage={i18n.autoAssignRules.noIdentitiesMatch as string}
                                        />
                                    ) : (
                                        filteredTestResults.map((item: TestResultItem) => (
                                            <TableRow key={item.identityId}>
                                                <TableCell className="font-medium">
                                                    <Link
                                                        to={routes.identityManagement.identities.detail(item.identityId)}
                                                        target="_blank"
                                                        className={`hover:underline text-primary`}
                                                        data-testid={`link-identity-detail-${item.identityId}`}
                                                    >
                                                        {item.identityDisplayName}
                                                    </Link>
                                                </TableCell>
                                                <TableCell className="text-muted-foreground">
                                                    <Link
                                                        to={routes.identityManagement.parties.detail(item.partyId)}
                                                        target="_blank"
                                                        className={`hover:underline`}
                                                        data-testid={`link-party-detail-${item.partyId}`}
                                                    >
                                                        {item.partyDisplayName}
                                                    </Link>
                                                </TableCell>
                                                <TableCell>
                                                    {getActionBadge(item.action)}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </ScrollableBox>

                        {/* Footer */}
                        <p><MutedText>{i18n.autoAssignRuleRequests.showingIdentities({filtered: filteredTestResults.length, total: testResults?.items?.length || 0})}</MutedText></p>
                    </Stack>
                </DialogContent>
            </Dialog>

            <InfoBanner variant="muted">
                <Stack gap="xs">
                    <SmallText><Strong>{i18n.autoAssignRuleRequests.expressionBeingTested}</Strong></SmallText>
                    <CodeBlock>{conditionExpr}</CodeBlock>
                </Stack>
            </InfoBanner>
        </Stack>
    )

    const renderAssignment = () => {
        if (ruleType === AutoAssignRuleType.FUNCTION_GROUP) {
            return renderFunctionGroupSelection()
        }
        return renderProfileSelection()
    }

    const functionGroupColumns: AssociationColumn<FunctionGroup>[] = [
        {
            header: i18n.common.name as string,
            render: (fg) => (
                <div>
                    <Strong>{fg.name}</Strong>
                    {fg.description && (
                        <MutedText size="xs" block className="truncate max-w-xs">{fg.description}</MutedText>
                    )}
                </div>
            ),
        },
        {
            header: i18n.common.type as string,
            // eslint-disable-next-line i18next/no-literal-string -- CSS className, not user text
            className: 'w-40',
            render: (fg) => fg.functionGroupType
                ? <Badge variant="outline">{labelOf(functionGroupTypes, fg.functionGroupType)}</Badge>
                : null,
        },
    ]

    const renderFunctionGroupSelection = () => (
        <Stack>
            <div>
                <Label>{i18n.autoAssignRuleRequests.functionGroupsLabel}</Label>
                <MutedText block className="mt-1 mb-3">
                    {i18n.autoAssignRuleRequests.functionGroupsDesc}
                </MutedText>
            </div>

            <AssociationEditor<FunctionGroup>
                items={selectedFunctionGroups}
                onSearch={setFunctionGroupSearchQuery}
                searchResults={(functionGroupSearchData?.content ?? []) as FunctionGroup[]}
                onAdd={(fg) => addFunctionGroup(fg.id)}
                onRemove={(fg) => removeFunctionGroup(fg.id)}
                columns={functionGroupColumns}
                getKey={(fg) => fg.id}
                getLabel={(fg) => fg.name}
                searchPlaceholder={i18n.autoAssignRuleRequests.searchFunctionGroupsToAdd as string}
                emptyMessage={i18n.autoAssignRuleRequests.selectAtLeastOneFunctionGroup as string}
                testId="function-group-association-editor"
            />
        </Stack>
    )

    const profileColumns: AssociationColumn<AccessProfileListItem>[] = [
        {
            header: i18n.common.name as string,
            render: (p) => (
                <div>
                    <Strong>{p.name}</Strong>
                    {p.description && (
                        <MutedText size="xs" block className="truncate max-w-xs">{p.description}</MutedText>
                    )}
                </div>
            ),
        },
        {
            header: i18n.accessProfiles.environment as string,
            // eslint-disable-next-line i18next/no-literal-string -- CSS className, not user text
            className: 'w-40',
            render: (p) => <Badge variant="secondary">{p.environment}</Badge>,
        },
    ]

    const renderProfileSelection = () => (
        <Stack>
            <div>
                <Label>{i18n.autoAssignRuleRequests.accessProfilesLabel}</Label>
                <MutedText block className="mt-1 mb-3">
                    {i18n.autoAssignRuleRequests.accessProfilesDesc}
                </MutedText>
            </div>

            <AssociationEditor<AccessProfileListItem>
                items={selectedProfiles}
                onSearch={setProfileSearchQuery}
                searchResults={profileSearchData?.content ?? []}
                onAdd={(p) => addProfile(p.id)}
                onRemove={(p) => removeProfile(p.id)}
                columns={profileColumns}
                getKey={(p) => p.id}
                getLabel={(p) => p.name}
                searchPlaceholder={i18n.autoAssignRuleRequests.searchProfilesToAdd as string}
                emptyMessage={i18n.autoAssignRuleRequests.selectAtLeastOneProfile as string}
                testId="profile-association-editor"
            />
        </Stack>
    )

    const renderReview = () => (
        <Stack gap="lg">
            <DetailGrid columns={2} responsive={false}>
                <div>
                    <MutedText className="font-medium block">{i18n.autoAssignRuleRequests.reviewRuleName}</MutedText>
                    <Strong>{name}</Strong>
                </div>
                <div>
                    <MutedText className="font-medium block">{i18n.autoAssignRuleRequests.reviewOwner}</MutedText>
                    <Strong>
                        {owners.length > 0
                            ? owners.map(o => ownerLabels[o.ownerPartyId] ?? o.ownerPartyId).join(', ')
                            : i18n.autoAssignRuleRequests.notSelected}
                    </Strong>
                </div>
                <div>
                    <MutedText className="font-medium block">{i18n.autoAssignRules.priority}</MutedText>
                    <Strong>{priority}</Strong>
                </div>
                <div>
                    <MutedText className="font-medium block">{i18n.autoAssignRules.removalGraceDays}</MutedText>
                    <Strong>{removalGraceDays}</Strong>
                </div>
            </DetailGrid>

            {description && (
                <div>
                    <MutedText className="font-medium block">{i18n.common.description}</MutedText>
                    <SmallText>{description}</SmallText>
                </div>
            )}

            <div>
                <MutedText block className="font-medium mb-2">{i18n.autoAssignRuleRequests.reviewConditionExpression}</MutedText>
                <CodeBlock>{conditionExpr}</CodeBlock>
            </div>

            {testResults && (
                <Alert className="bg-status-success-subtle border-green-200">
                    <Icon icon={Users} className="text-status-success"/> <AlertTitle className="text-status-success">{i18n.autoAssignRules.testResults}</AlertTitle>
                    <AlertDescription className="text-status-success">
                        {i18n.autoAssignRuleRequests.reviewMatchedOfTotal({matched: testResults.matched, total: testResults.totalEvaluated})}
                        {testResults.toAdd > 0 && ` ${i18n.autoAssignRuleRequests.reviewWouldBeAdded({count: testResults.toAdd})}`}
                    </AlertDescription>
                </Alert>
            )}

            <div>
                <MutedText block className="font-medium mb-2">
                    {ruleType === AutoAssignRuleType.FUNCTION_GROUP
                        ? i18n.autoAssignRuleRequests.reviewFunctionGroups({count: selectedFunctionGroups.length})
                        : i18n.autoAssignRuleRequests.reviewAccessProfiles({count: selectedProfiles.length})}
                </MutedText>
                <InlineRow wrap gap="sm">
                    {ruleType === AutoAssignRuleType.FUNCTION_GROUP
                        ? selectedFunctionGroups.map((functionGroup) => (
                            <Badge key={functionGroup.id} variant="outline">
                                {functionGroup.name}
                                {functionGroup.functionGroupType && (
                                    <MutedText size="xs" className="ml-1 opacity-70">{labelOf(functionGroupTypes, functionGroup.functionGroupType)}</MutedText>
                                )}
                            </Badge>
                        ))
                        : selectedProfiles.map((profile) => (
                            <Badge key={profile.id} variant="outline">
                                {profile.name}
                            </Badge>
                        ))}
                </InlineRow>
            </div>
        </Stack>
    )

    const wizardTitle = isEditMode
        ? i18n.autoAssignRuleRequests.wizardEditTitle({name: name || i18n.autoAssignRuleRequests.defaultName})
        : isUpdateFromUrl
            ? i18n.autoAssignRuleRequests.wizardUpdateTitle
            : i18n.autoAssignRuleRequests.wizardTitle

    const wizardStepDefs: WizardStepType[] = steps.map(s => ({id: s.id, title: String(s.title)}))

    const wizardNavigation = (
        <>
            <Button
                variant="outline"
                onClick={goBack}
                disabled={currentStep === AutoAssignWizardStep.BASICS}
                data-testid="btn-previous-step"
            >
                <Icon icon={ArrowLeft} className="mr-2"/>
                {i18n.common.back}
            </Button>
            {currentStep < steps.length - 1 ? (
                <Button
                    onClick={goNext}
                    disabled={!isStepValid(currentStep)}
                    data-testid="btn-next-step"
                >
                    {i18n.autoAssignRules.next}
                    <Icon icon={ArrowRight} className="ml-2"/>
                </Button>
            ) : (
                <Button
                    onClick={handleSubmit}
                    disabled={saveMutation.isPending || !isStepValid(currentStep)}
                    data-testid="btn-submit"
                >
                    {saveMutation.isPending ? (
                        <>
                            <ButtonSpinner/>
                            {isEditMode ? i18n.common.saving : i18n.common.creating}
                        </>
                    ) : (
                        <>
                            <Icon icon={CheckCircle} className="mr-2"/>
                            {isEditMode ? i18n.common.saveChanges : i18n.autoAssignRuleRequests.createRequest}
                        </>
                    )}
                </Button>
            )}
        </>
    )

    return (
        <PageLayout
            variant="wizard"
            title={String(wizardTitle)}
            icon={CONCEPT_ICON.autoAssignRuleRequest}
            backTo={true}
            loading={!!isUpdateFromUrl && (ruleLoading || ruleProfilesLoading)}
            wizardSteps={wizardStepDefs}
            wizardCurrentStep={currentStep}
            wizardNavigation={wizardNavigation}
            data-testid="auto-assign-rule-request-create-wizard-page"
        >
            <div ref={stepContainerRef} data-testid="auto-assign-rule-request-step-container">
                <WizardStepHeading
                    title={steps[currentStep].title}
                    description={steps[currentStep].description}
                />
                {renderStepContent()}
            </div>
        </PageLayout>
    )
}
